import React, {useMemo} from "react";
import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import {useTranslation} from "react-i18next";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import classes from "./index.module.css";
import ProjectCard from "../../components/ProjectCard";
import {useQuery} from "@apollo/client";
import {query, QueryData} from "./index.graphql";
import {Project, Challenge} from "../../schema";
import ChallengeCard from "../../components/ChallengeCard";
import Breadcrumbs from "../../ui/breadcrumbs";
import {queryByTypenames, getMapByObjectProperty} from "../../utils";
import useSortedChallenges from "../../hooks/useSortedChallenges";

export default function PlayerIndexScene() {
  const {t} = useTranslation();

  const {data, loading: fetching} = useQuery<QueryData>(query, {fetchPolicy: "network-only"});
  const {items, challengesStates} = data ?? {};

  const mappedChallengesStates = useMemo(() => {
    if (!challengesStates) {
      return undefined;
    }
    return getMapByObjectProperty(challengesStates, "challengeId");
  }, [challengesStates]);

  const loading = !items && fetching;

  const [challenges, projects] = useMemo(() => {
    if (!items || items.length === 0) {
      return [[], []];
    }
    return queryByTypenames(items, ["Challenge", "Project"]) as [Challenge[], Project[]];
  }, [items]);

  const [activeChallenges, completedChallenges] = useSortedChallenges(challenges, mappedChallengesStates);

  const [activeProjects, completedProjects] = useMemo(() => {
    const active = projects.filter(p => !p.hasPassed).sort((a, b) => {
      const aDate = a.participationCreatedAt as number | undefined;
      const bDate = b.participationCreatedAt as number | undefined;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    });
    const completed = projects.filter(p => p.hasPassed).sort((a, b) => {
      const aDate = a.participationCreatedAt as number | undefined;
      const bDate = b.participationCreatedAt as number | undefined;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return bDate - aDate;
    });
    return [active, completed];
  }, [projects]);

  return (
    <>
      <ReactDocumentTitle title={t("playerIndex.title")} />

      <Layout>
        <Header />
        <Main>
          <Container className={classes.main}>
            <Container className={classes.projects}>
              <Breadcrumbs className={classes.breadcrumbs}>
                <span>{t("breadcrumbs.player")}</span>
              </Breadcrumbs>
              <h1 className={classes.h1}>{t("playerIndex.projects")}</h1>

              {loading && (
                <div className={classes.projectItems}>
                  <ProjectCard.Skeleton className={classes.projectItem}/>
                  <ProjectCard.Skeleton className={classes.projectItem}/>
                  <ProjectCard.Skeleton className={classes.projectItem}/>
                </div>
              )}

              {!loading && (
                <>
                  {activeProjects.length === 0 && completedProjects.length === 0 && (
                    <div className={classes.emptyState}>{t("playerIndex.emptyState")}</div>
                  )}

                  {activeProjects.length > 0 && (
                    <>
                      <h2 className={classes.assignedCompletedHeader}>{t("playerIndex.assigned")}</h2>
                      <div className={classes.projectItems}>
                        {activeProjects.map((item) => (
                          <ProjectCard
                            key={item.id}
                            className={classes.projectItem}
                            project={item}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {completedProjects.length > 0 && (
                    <>
                      <h2 className={classes.assignedCompletedHeader}>{t("playerIndex.completed")}</h2>
                      <div className={classes.projectItems}>
                        {completedProjects.map((item) => (
                          <ProjectCard
                            key={item.id}
                            className={classes.projectItem}
                            project={item}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </Container>
            <Container className={classes.challenges}>
              <h1 className={classes.h1}>{t("playerIndex.challenges")}</h1>

              {loading && (
                <div className={classes.challengeItems}>
                  <ChallengeCard.Skeleton className={classes.challengeItem} />
                  <ChallengeCard.Skeleton className={classes.challengeItem} />
                  <ChallengeCard.Skeleton className={classes.challengeItem} />
                  <ChallengeCard.Skeleton className={classes.challengeItem} />
                  <ChallengeCard.Skeleton className={classes.challengeItem} />
                </div>
              )}

              {!loading && (
                <>
                  {activeChallenges.length === 0 && completedChallenges.length === 0 && (
                    <div className={classes.emptyState}>{t("playerIndex.emptyState")}</div>
                  )}

                  {activeChallenges.length > 0 && (
                    <>
                      <h2 className={classes.assignedCompletedHeader}>{t("playerIndex.assigned")}</h2>
                      <div className={classes.challengeItems}>
                        {activeChallenges.map((item) => (
                          <ChallengeCard
                            key={item.id}
                            className={classes.challengeItem}
                            challenge={item}
                            challengeState={mappedChallengesStates!.get(item.id)!}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {completedChallenges.length > 0 && (
                    <>
                      <h2 className={classes.assignedCompletedHeader}>{t("playerIndex.completed")}</h2>
                      <div className={classes.challengeItems}>
                        {completedChallenges.map((item) => (
                          <ChallengeCard
                            key={item.id}
                            className={classes.challengeItem}
                            challenge={item}
                            challengeState={mappedChallengesStates!.get(item.id)!}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </Container>
          </Container>
        </Main>
      </Layout>
    </>
  );
}
